import { PlansScreen } from '../../../../../analytics/plansEvents'
import { useRef } from 'react'
import type { LumpsumAllocationSellPlanRecommendationDetails } from '../../../api/allocationRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { buildAllocationSellRecommendationPair } from '../../../pair/buildPlanRecommendationPair'
import { PlanTaxLiability } from '../../../shared/tax/PlanTaxLiability'
import { ReasonsToBuyAccordion } from '../../../shared/reasons/ReasonsToBuyAccordion'
import { ReasonsToSellAccordion } from '../../../shared/reasons/ReasonsToSellAccordion'
import { StepsToRebalanceAccordion, StepsAccordionPlanType } from '../../../shared/steps/StepsToRebalanceAccordion'
import { PlanUnderlineTabs } from '../../../shared/sheet/PlanUnderlineTabs'
import { useScrollToFirstAccordionOnTabChange } from '../../../shared/sheet/planRecommendationSheetScroll'

type AllocationSellPlanRecommendationBodyProps = {
  planDetails: LumpsumAllocationSellPlanRecommendationDetails | null
  selectedBuyIndex: number
  onSelectBuyIndex: (selectedIndex: number) => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

export function AllocationSellPlanRecommendationBody({
  planDetails,
  selectedBuyIndex,
  onSelectBuyIndex,
  onMissedGainsInfoClick
}: AllocationSellPlanRecommendationBodyProps) {
  const buyFunds = planDetails?.fundsToBeBought ?? []
  const stickyTabsRef = useRef<HTMLDivElement>(null)
  const firstAccordionRef = useRef<HTMLDivElement>(null)
  useScrollToFirstAccordionOnTabChange(stickyTabsRef, firstAccordionRef, selectedBuyIndex)

  const taxData = planDetails?.consolidatedTax ?? buyFunds[0]?.taxData ?? null
  const areAllSellActionsCompleted = planDetails?.executed === true
  // Outside tax uses full-sheet redeem (root totalTransferAmount), not per-leg pair amounts.
  const taxWithdrawalAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  // 1:1 → root side; 1:m → counter (buy) side
  const detailsTitle = buyFunds.length > 1 ? 'Buy details' : 'Sell details'

  return (
    <>
      <div className="h-[10px] bg-[#F0F4F7] mt-6" />

      <div ref={stickyTabsRef} className="sticky top-0 z-[1] bg-white pt-5">
        <div className="px-5 pb-6 pt-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
          {detailsTitle}
        </div>
        <PlanUnderlineTabs
          analyticsScreen={PlansScreen.LumpsumAllocationRecommendation}
          funds={buyFunds.map((fund) => ({
            fundName: fund?.fundName ?? '',
            fundIsin: fund?.isin ?? '',
            mfLogoUrl: fund?.mfLogoUrl ?? ''
          }))}
          activeTabIndex={selectedBuyIndex}
          onChange={onSelectBuyIndex}
        />
      </div>

      <div ref={firstAccordionRef} aria-hidden />

      {buyFunds.map((buyFund, buyFundIndex) => {
        const pair = buildAllocationSellRecommendationPair(planDetails, buyFund)
        if (!pair) {
          return null
        }

        return (
          <div
            key={buyFund?.isin ?? String(buyFundIndex)}
            className={buyFundIndex === selectedBuyIndex ? undefined : 'hidden'}
            aria-hidden={buyFundIndex !== selectedBuyIndex}
          >
            <ReasonsToSellAccordion
              pair={pair}
              onMissedGainsInfoClick={onMissedGainsInfoClick}
            />
            <ReasonsToBuyAccordion pair={pair} />
            <StepsToRebalanceAccordion
              stepsPlanType={StepsAccordionPlanType.REALLOCATE}
              pair={pair}
            />
          </div>
        )
      })}

      <PlanTaxLiability
        isOutside
        areAllSellActionsCompleted={areAllSellActionsCompleted}
        fundName={planDetails?.fundName ?? ''}
        fundIsin={planDetails?.isin ?? ''}
        tax={{
          taxData,
          withdrawalAmount: taxWithdrawalAmount,
          currentValue: planDetails?.currentValue ?? taxData?.currentValue ?? null
        }}
      />
    </>
  )
}
